import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Command, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SearchResults } from './SearchResults';
import { SearchSuggestions } from './SearchSuggestions';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import type { SearchResult, SearchFilters } from '@/hooks/useGlobalSearch';
import { useAccessibleMotion } from '@/hooks/useAccessibleMotion';

interface GlobalSearchProps {
  className?: string;
  placeholder?: string;
  showMobileSearch?: boolean;
  onSearchOpen?: () => void;
  onSearchClose?: () => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  className = '',
  placeholder = 'Search galleries, services, leads...',
  showMobileSearch = false,
  onSearchOpen,
  onSearchClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { prefersReducedMotion } = useAccessibleMotion();

  const {
    query,
    results,
    isSearching,
    suggestions,
    filters,
    totalResults,
    recentSearches,
    setQuery,
    setFilters,
    navigateToResult,
    clearSearch,
    clearRecentSearches,
  } = useGlobalSearch();

  // Handle keyboard shortcuts
  const handleSearchOpen = useCallback(() => {
    setIsOpen(true);
    onSearchOpen?.();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [onSearchOpen]);

  const handleSearchClose = useCallback(() => {
    setIsOpen(false);
    setInputValue('');
    clearSearch();
    onSearchClose?.();
  }, [clearSearch, onSearchClose]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleSearchOpen();
      }

      // Escape to close search
      if (e.key === 'Escape' && isOpen) {
        handleSearchClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, handleSearchOpen, handleSearchClose]);

  // Handle clicks outside search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        handleSearchClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleSearchClose]);

  // Sync input value with query
  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setQuery(value);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setQuery(suggestion);
    inputRef.current?.focus();
  };

  const handleResultClick = (result: SearchResult) => {
    navigateToResult(result);
    handleSearchClose();
  };

  const handleFiltersChange = (newFilters: Partial<SearchFilters>) => {
    setFilters(newFilters);
  };

  const shouldShowResults = query.length >= 2;

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      {/* Search Input */}
      <div className='relative'>
        <motion.div
          className='absolute inset-y-0 left-3 flex items-center pointer-events-none'
          {...(!prefersReducedMotion && {
            animate: isSearching ? { rotate: 360 } : { rotate: 0 },
            transition: {
              duration: 1,
              repeat: isSearching ? Infinity : 0,
              ease: 'linear',
            },
          })}>
          {isSearching ? (
            <Loader2 className='w-4 h-4 text-muted-foreground' />
          ) : (
            <Search className='w-4 h-4 text-muted-foreground' />
          )}
        </motion.div>

        <Input
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleSearchOpen}
          className='w-full pl-10 pr-20 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors'
        />

        {/* Right side controls */}
        <div className='absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2'>
          {/* Clear button */}
          <AnimatePresence>
            {inputValue && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.15 }}>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-6 w-6 p-0 hover:bg-muted'
                  onClick={() => handleInputChange('')}>
                  <X className='w-3 h-3' />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Keyboard shortcut hint */}
          {!isOpen && (
            <div className='hidden md:flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1'>
              <Command className='w-3 h-3' />
              <span>K</span>
            </div>
          )}
        </div>
      </div>

      {/* Search Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: prefersReducedMotion ? 0 : -10,
              scale: prefersReducedMotion ? 1 : 0.95,
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: prefersReducedMotion ? 0 : -10,
              scale: prefersReducedMotion ? 1 : 0.95,
            }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            className='absolute top-full left-0 right-0 mt-2 z-dropdown'>
            {shouldShowResults ? (
              <SearchResults
                results={results}
                isSearching={isSearching}
                query={query}
                totalResults={totalResults}
                filters={filters}
                onResultClick={handleResultClick}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <SearchSuggestions
                suggestions={suggestions}
                recentSearches={recentSearches}
                onSuggestionClick={handleSuggestionClick}
                onClearRecent={clearRecentSearches}
                query={query}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && showMobileSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            className='fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden'
            onClick={handleSearchClose}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Mobile search modal for smaller screens
export const MobileSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { prefersReducedMotion } = useAccessibleMotion();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
          className='fixed inset-0 bg-background z-50 md:hidden'>
          <div className='p-4'>
            <div className='flex items-center gap-3 mb-4'>
              <Button
                variant='ghost'
                size='icon'
                onClick={onClose}
                className='flex-shrink-0'>
                <X className='w-5 h-5' />
              </Button>
              <div className='flex-1'>
                <GlobalSearch
                  placeholder='Search everything...'
                  className='w-full'
                  onSearchClose={onClose}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearch;
